/**
 * InventoryCrudService — port of BigxBookApp/src/inventory/InventoryCrudService.ts
 * Adapted for the Electron renderer: same WatermelonDB write semantics,
 * same isSynced/version bookkeeping for CRDT sync.
 */
import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import { Product, Category, Batch, StockMovement } from '@/db/models/Inventory';
import { logActivity, prepareAuditLog } from '@/utils/auditLog';

export type ProductInput = {
  storeId: string;
  categoryId?: string | null;
  supplierId?: string | null;
  name: string;
  barcode?: string | null;
  sku?: string | null;
  hsnCode?: string | null;
  mrp: number;
  sellingPrice: number;
  purchasePrice: number;
  currentStock: number;
  lowStockAlert?: number;
  trackInventory?: boolean;
  isService?: boolean;
  unit: string;
  gstRate: number;
  cessRate?: number;
  taxProfileId?: string | null;
  isActive?: boolean;
};

export async function createProduct(details: ProductInput): Promise<Product> {
  const product = await txn(async (db) => {
    return db.get<Product>('products').create((p: Product) => {
      p.storeId = details.storeId;
      p.categoryId = details.categoryId ?? null;
      p.supplierId = details.supplierId ?? null;
      p.name = details.name;
      p.barcode = details.barcode ?? null;
      p.sku = details.sku ?? null;
      p.hsnCode = details.hsnCode ?? null;
      p.mrp = details.mrp;
      p.sellingPrice = details.sellingPrice;
      p.purchasePrice = details.purchasePrice;
      p.currentStock = details.currentStock;
      p.lowStockAlert = details.lowStockAlert ?? 5;
      p.trackInventory = details.trackInventory ?? true;
      p.isService = details.isService ?? false;
      p.unit = details.unit;
      p.gstRate = details.gstRate;
      p.cessRate = details.cessRate ?? 0;
      p.taxProfileId = details.taxProfileId ?? null;
      p.isActive = details.isActive ?? true;
      p.lastSyncCurrentStock = 0;
      p.isSynced = false;
      p.version = 1;
    });
  });

  void logActivity({
    action: 'product_created',
    entityType: 'product',
    entityId: product.id,
    storeId: details.storeId,
    changes: {
      summary: `${details.name} · MRP ${details.mrp} · stock ${details.currentStock} ${details.unit}`,
      quantity: details.currentStock,
      after: {
        name: details.name,
        sellingPrice: details.sellingPrice,
        mrp: details.mrp,
        purchasePrice: details.purchasePrice,
        currentStock: details.currentStock,
        gstRate: details.gstRate,
      },
    },
  });

  return product;
}

/** Fields we surface in the activity log's before/after diff for product edits. */
const PRODUCT_DIFF_FIELDS: (keyof ProductInput)[] = [
  'name', 'sellingPrice', 'mrp', 'purchasePrice', 'currentStock',
  'gstRate', 'cessRate', 'lowStockAlert', 'sku', 'barcode', 'hsnCode', 'isActive',
];

export async function updateProduct(product: Product, updates: Partial<ProductInput>): Promise<Product> {
  // Snapshot the "before" values for changed fields before the write mutates them.
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  for (const key of PRODUCT_DIFF_FIELDS) {
    if (updates[key] === undefined) continue;
    const old = (product as any)[key];
    if (old !== updates[key]) {
      before[key] = old;
      after[key] = updates[key];
    }
  }
  const productName = updates.name ?? product.name;

  await txn(async (db) => {
    await product.update((r: Product) => {
      if (updates.name !== undefined) r.name = updates.name;
      if (updates.categoryId !== undefined) r.categoryId = updates.categoryId;
      if (updates.barcode !== undefined) r.barcode = updates.barcode;
      if (updates.sku !== undefined) r.sku = updates.sku;
      if (updates.hsnCode !== undefined) r.hsnCode = updates.hsnCode;
      if (updates.mrp !== undefined) r.mrp = updates.mrp;
      if (updates.sellingPrice !== undefined) r.sellingPrice = updates.sellingPrice;
      if (updates.purchasePrice !== undefined) r.purchasePrice = updates.purchasePrice;
      if (updates.currentStock !== undefined) r.currentStock = updates.currentStock;
      if (updates.lowStockAlert !== undefined) r.lowStockAlert = updates.lowStockAlert;
      if (updates.trackInventory !== undefined) r.trackInventory = updates.trackInventory;
      if (updates.isService !== undefined) r.isService = updates.isService;
      if (updates.unit !== undefined) r.unit = updates.unit;
      if (updates.gstRate !== undefined) r.gstRate = updates.gstRate;
      if (updates.cessRate !== undefined) r.cessRate = updates.cessRate;
      if (updates.taxProfileId !== undefined) r.taxProfileId = updates.taxProfileId;
      if (updates.isActive !== undefined) r.isActive = updates.isActive;
      r.isSynced = false;
      r.version += 1;
    });
    return product;
  });

  // Only log when something number/identity-relevant actually changed.
  if (Object.keys(after).length > 0) {
    void logActivity({
      action: 'product_updated',
      entityType: 'product',
      entityId: product.id,
      storeId: product.storeId,
      changes: {
        summary: `${productName} · ${Object.keys(after).join(', ')} changed`,
        before,
        after,
      },
    });
  }

  return product;
}

export async function deleteProduct(product: Product): Promise<void> {
  // Active dependency check (sales / POs) — same rule as BigxBook.
  const salesCount = await database.get('line_items')
    .query(Q.where('product_id', product.id))
    .fetchCount();
  if (salesCount > 0) {
    throw new Error(`Deletion denied: product appears on ${salesCount} invoice(s). Mark it inactive instead.`);
  }
  const poCount = await database.get('po_line_items')
    .query(Q.where('product_id', product.id))
    .fetchCount();
  if (poCount > 0) {
    throw new Error(`Deletion denied: product appears on ${poCount} purchase order(s). Mark it inactive instead.`);
  }

  const snapshot = { id: product.id, name: product.name, storeId: product.storeId };

  await txn(async (db) => {
    if (product.isSynced) {
      await product.update((r: Product) => {
        r.isDeleted = true;
        r.isSynced = false;
        r.version += 1;
      });
    } else {
      await product.destroyPermanently();
    }
  });

  void logActivity({
    action: 'product_deleted',
    entityType: 'product',
    entityId: snapshot.id,
    storeId: snapshot.storeId,
    changes: { summary: `${snapshot.name} removed from catalog` },
  });
}

// ── Stock adjustment ───────────────────────────────────────────────────
export async function adjustStock(
  product: Product,
  quantityDelta: number,
  reason: string,
  options: { godownId?: string | null; batchId?: string | null; userId?: string | null } = {},
): Promise<void> {
  if (quantityDelta === 0) return;
  const stockBefore = product.currentStock;
  const productName = product.name;
  await txn(async (db) => {
    const ops: any[] = [];

    const newStock = Math.max(0, product.currentStock + quantityDelta);
    ops.push(product.prepareUpdate((p: Product) => {
      p.currentStock = newStock;
      p.isSynced = false;
      p.version += 1;
    }));

    if (options.batchId) {
      try {
        const batch = await db.get<Batch>('batches').find(options.batchId);
        const newBatchQty = Math.max(0, batch.quantity + quantityDelta);
        ops.push(batch.prepareUpdate((b: Batch) => {
          b.quantity = newBatchQty;
          b.isSynced = false;
          b.version += 1;
        }));
      } catch {
        /* batch missing — ignore */
      }
    }

    ops.push(db.get<StockMovement>('stock_movements').prepareCreate((sm: StockMovement) => {
      sm.productId = product.id;
      sm.godownId = options.godownId ?? null;
      sm.batchId = options.batchId ?? null;
      sm.type = 'ADJUSTMENT';
      sm.quantity = quantityDelta;
      sm.referenceId = null;
      sm.referenceType = 'MANUAL';
      sm.notes = reason;
      sm.source = 'DESKTOP';
      sm.userId = options.userId ?? null;
      sm.isSynced = false;
      sm.version = 1;
      sm.isDeleted = false;
    }));

    ops.push(prepareAuditLog({
      action: 'stock_adjusted',
      entityType: 'product',
      entityId: product.id,
      userId: options.userId ?? null,
      changes: {
        summary: `${productName} · ${quantityDelta > 0 ? '+' : ''}${quantityDelta} (${stockBefore} → ${newStock}) · ${reason}`,
        quantity: quantityDelta,
        ref: reason,
        before: { currentStock: stockBefore },
        after: { currentStock: newStock },
      },
    }));

    await db.batch(...ops);
  });
}

// ── Categories ─────────────────────────────────────────────────────────
export async function createCategory(merchantId: string, name: string): Promise<Category> {
  return txn(async (db) => {
    return db.get<Category>('categories').create((c: Category) => {
      c.merchantId = merchantId;
      c.name = name.trim();
      c.parentId = null;
      c.sortOrder = 0;
      c.isSynced = false;
      c.version = 1;
      c.isDeleted = false;
    });
  });
}

export async function deleteCategory(category: Category): Promise<void> {
  await txn(async (db) => {
    if (category.isSynced) {
      await category.update((c: Category) => {
        c.isDeleted = true;
        c.isSynced = false;
        c.version += 1;
      });
    } else {
      await category.destroyPermanently();
    }
  });
}
